import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}


/**
 * Format a date for display
 */
export function formatDate(date: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
    if (!date) return ''
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString('en-US', options ?? { year: 'numeric', month: 'long', day: 'numeric' })
}

/**
 * Get initials from a name
 */
export function getInitials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}

/**
 * Get a time-of-day greeting
 */
export function getGreeting(): string {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 17) return 'Good afternoon'
    return 'Good evening'
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "yesterday", "3 days ago")
 */
export function timeAgo(date: Date | string | null | undefined): string {
    if (!date) return ''
    const d = typeof date === 'string' ? new Date(date) : date
    const now = new Date()
    const seconds = Math.floor((now.getTime() - d.getTime()) / 1000)

    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days === 1) return 'yesterday'
    if (days < 7) return `${days}d ago`
    return formatDate(d, { month: 'short', day: 'numeric' })
}

/**
 * Validate that a string is a valid CUID format.
 * CUIDs start with 'c' and are 25 characters of lowercase alphanumeric.
 */
export function isValidCuid(id: string): boolean {
    return /^c[a-z0-9]{24}$/.test(id)
}

/**
 * Whether a string is a plausible Tenant id. The schema default is cuid(),
 * but the earliest tenants (temple-beth-israel) were onboarded with UUID
 * ids, so prod holds both formats — accept either. This is input hygiene
 * only; authorization is the tenant-membership check at each call site.
 */
export function isValidTenantId(id: string): boolean {
    return (
        isValidCuid(id) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(id)
    )
}

/**
 * Generate a consistent color from a string (for avatars)
 */
export function stringToColor(str: string): string {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }
    const hue = hash % 360
    return `hsl(${hue}, 65%, 45%)`
}
